import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';
import type {
  DashboardMetrics,
  SystemHealth,
  ActivityLog,
  AdminUser,
  CreateAdminUserRequest,
  UpdateAdminUserRequest,
  CandidateProfile,
  UpdateSystemSettingsRequest,
  ExportRequest,
  ExportResponse,
  MessageResponse,
  JobApplicationDetails,
} from './types';

/**
 * AdminModule - Handles all administrative operations
 * 
 * This module provides comprehensive admin functionality including:
 * - Dashboard metrics and system health monitoring
 * - Admin user management
 * - Candidate profile management
 * - System settings management
 * - Data export functionality
 */
export class AdminModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // Dashboard and System Health
  // ============================================================================

  /**
   * Get dashboard metrics
   * GET /api/admin/dashboard/metrics
   */
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    return this.get<DashboardMetrics>('/api/admin/dashboard/metrics');
  }

  /**
   * Get system health status
   * GET /api/admin/system/health
   */
  async getSystemHealth(): Promise<SystemHealth> {
    return this.get<SystemHealth>('/api/admin/system/health');
  }

  /**
   * Get recent activity logs
   * GET /api/admin/activity
   */
  async getRecentActivity(): Promise<ActivityLog[]> {
    return this.get<ActivityLog[]>('/api/admin/activity');
  }

  // ============================================================================
  // Admin User Management
  // ============================================================================

  /**
   * Get all admin users
   * GET /api/admin/users
   */
  async getAdminUsers(): Promise<AdminUser[]> {
    return this.get<AdminUser[]>('/api/admin/users');
  }

  /**
   * Create a new admin user
   * POST /api/admin/users
   */
  async createAdminUser(data: CreateAdminUserRequest): Promise<AdminUser> {
    return this.post<AdminUser>('/api/admin/users', data);
  }

  /**
   * Update an admin user
   * PUT /api/admin/users/:id
   */
  async updateAdminUser(id: string, data: UpdateAdminUserRequest): Promise<AdminUser> {
    return this.put<AdminUser>(`/api/admin/users/${id}`, data);
  }

  /**
   * Delete an admin user
   * DELETE /api/admin/users/:id
   */
  async deleteAdminUser(id: string): Promise<MessageResponse> {
    return this.delete<MessageResponse>(`/api/admin/users/${id}`);
  }

  /**
   * Toggle admin user status (active/inactive)
   * PATCH /api/admin/users/:id/toggle-status
   */
  async toggleUserStatus(id: string): Promise<AdminUser> {
    return this.patch<AdminUser>(`/api/admin/users/${id}/toggle-status`);
  }

  // ============================================================================
  // Candidate Management
  // ============================================================================

  /**
   * Get all candidates with filtering and pagination
   * GET /api/admin/candidates
   */
  async getCandidates(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<CandidateProfile[]> {
    const queryString = params ? this.buildQueryString(params) : '';
    const endpoint = queryString ? `/api/admin/candidates?${queryString}` : '/api/admin/candidates';
    return this.get<CandidateProfile[]>(endpoint);
  }

  /**
   * Get detailed candidate profile
   * GET /api/admin/candidates/:id
   */
  async getCandidateDetails(id: string): Promise<CandidateProfile> {
    return this.get<CandidateProfile>(`/api/admin/candidates/${id}`);
  }

  /**
   * Get all conversations (admin only)
   * GET /api/admin/conversations
   */
  async getAllConversations(): Promise<Array<{
    user_id: string;
    user_name: string;
    user_email: string;
    last_message: string;
    last_message_at: string;
    unread_count: number;
  }>> {
    return this.get('/api/admin/conversations');
  }

  /**
   * Get all applications for a specific job (admin only)
   * GET /api/admin/jobs/:id/applications
   */
  async getJobApplications(jobId: string): Promise<JobApplicationDetails[]> {
    return this.get<JobApplicationDetails[]>(`/api/admin/jobs/${jobId}/applications`);
  }

  // ============================================================================
  // System Settings
  // ============================================================================

  /**
   * Get all system settings
   * GET /api/admin/settings
   * Returns a simple key-value map
   */
  async getSystemSettings(): Promise<Record<string, string>> {
    return this.get<Record<string, string>>('/api/admin/settings');
  }

  /**
   * Update system settings
   * PUT /api/admin/settings
   */
  async updateSystemSettings(request: UpdateSystemSettingsRequest): Promise<MessageResponse> {
    return this.put<MessageResponse>('/api/admin/settings', request);
  }

  /**
   * Test service connection
   * POST /api/admin/settings/test-connection
   */
  async testServiceConnection(service: string): Promise<any> {
    return this.post<any>('/api/admin/settings/test-connection', { service });
  }

  // ============================================================================
  // Google OAuth
  // ============================================================================

  /**
   * Get Google OAuth authorization URL
   * GET /api/admin/settings/google/auth-url
   */
  async getGoogleAuthUrl(): Promise<{ auth_url: string; redirect_uri: string }> {
    return this.get<{ auth_url: string; redirect_uri: string }>('/api/admin/settings/google/auth-url');
  }

  /**
   * Get Google connection status
   * GET /api/admin/settings/google/status
   */
  async getGoogleConnectionStatus(): Promise<{
    connected: boolean;
    connected_account: string | null;
    connection_valid: boolean;
  }> {
    return this.get('/api/admin/settings/google/status');
  }

  /**
   * Disconnect Google account
   * POST /api/admin/settings/google/disconnect
   */
  async disconnectGoogleAccount(): Promise<{ success: boolean; message: string }> {
    return this.post('/api/admin/settings/google/disconnect', {});
  }

  // ============================================================================
  // Data Export
  // ============================================================================

  /**
   * Export jobs data
   * GET /api/admin/export/jobs
   */
  async exportJobs(request: ExportRequest): Promise<ExportResponse> {
    const queryString = this.buildQueryString(request);
    return this.get<ExportResponse>(`/api/admin/export/jobs?${queryString}`);
  }

  /**
   * Export applications data
   * GET /api/admin/export/applications
   */
  async exportApplications(request: ExportRequest): Promise<ExportResponse> {
    const queryString = this.buildQueryString(request);
    return this.get<ExportResponse>(`/api/admin/export/applications?${queryString}`);
  }

  /**
   * Export candidates data
   * GET /api/admin/export/candidates
   */
  async exportCandidates(request: ExportRequest): Promise<ExportResponse> {
    const queryString = this.buildQueryString(request);
    return this.get<ExportResponse>(`/api/admin/export/candidates?${queryString}`);
  }

  // ============================================================================
  // AI Generation
  // ============================================================================

  /**
   * Generate email content using AI
   * POST /api/admin/ai/generate-email
   */
  async generateEmail(request: import('./types').GenerateEmailRequest): Promise<import('./types').GenerateEmailResponse> {
    return this.post<import('./types').GenerateEmailResponse>('/api/admin/ai/generate-email', request);
  }

  /**
   * Suggest message response using AI
   * POST /api/admin/ai/suggest-response
   */
  async suggestResponse(request: import('./types').SuggestResponseRequest): Promise<import('./types').SuggestResponseResponse> {
    return this.post<import('./types').SuggestResponseResponse>('/api/admin/ai/suggest-response', request);
  }

  /**
   * Generate job description using AI
   * POST /api/admin/jobs/ai/generate-description
   */
  async generateJobDescription(request: import('./types').GenerateJobDescriptionRequest): Promise<import('./types').GenerateJobDescriptionResponse> {
    return this.post<import('./types').GenerateJobDescriptionResponse>('/api/admin/jobs/ai/generate-description', request);
  }

  /**
   * Generate offer letter using AI
   * POST /api/admin/ai/generate-offer-letter
   */
  async generateOfferLetter(request: import('./types').GenerateOfferLetterRequest): Promise<import('./types').GenerateOfferLetterResponse> {
    return this.post<import('./types').GenerateOfferLetterResponse>('/api/admin/ai/generate-offer-letter', request);
  }

  /**
   * Generate social media images using AI
   * POST /api/admin/ai/generate-social-images
   */
  async generateSocialImages(request: import('./types').GenerateSocialImagesRequest): Promise<import('./types').GenerateSocialImagesResponse> {
    return this.post<import('./types').GenerateSocialImagesResponse>('/api/admin/ai/generate-social-images', request);
  }

  // ============================================================================
  // Interview Scheduling
  // ============================================================================

  /**
   * Schedule an interview
   * POST /api/admin/interviews
   */
  async scheduleInterview(request: import('./types').ScheduleInterviewRequest): Promise<import('./types').ScheduleInterviewResponse> {
    return this.post<import('./types').ScheduleInterviewResponse>('/api/admin/interviews', request);
  }

  /**
   * Get interviews for a candidate
   * GET /api/admin/interviews/:candidateId
   */
  async getInterviewsByCandidate(candidateId: string): Promise<import('./types').InterviewListResponse> {
    return this.get<import('./types').InterviewListResponse>(`/api/admin/interviews/${candidateId}`);
  }
}
